@import 'apps/webcomponents/src/assets/style-helpers.scss';
@import 'apps/webcomponents/src/assets/flexbox-helpers.scss';
@import 'apps/webcomponents/src/assets/scss/mixins';
@import 'apps/webcomponents/src/assets/common.scss';

:host {
  --border-radius: 4px;
  --input-height: 32px;
  --input-btn-width: 24px;
  --input-btn-height: 24px;
}

input:-webkit-autofill {
  box-shadow: 0 0 0 1000px var(--white-layout-301-level-0-elevation) inset !important; /* Reset background */
  -webkit-text-fill-color: var(--body-text-arrows-400) !important;
  padding: calc(var(--spacer) - 3px);
}

.input {
  color: var(--body-text-arrows-400);
  font-size: var(--body-size);
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  transition: all 0.2s ease;
  border-radius: var(--border-radius);
  border-top: var(--custom-input-border-top, var(--input-border, 1px solid var(--chart-100-pressed)));
  border-bottom: var(--custom-input-border-bottom, var(--input-border, 1px solid var(--chart-100-pressed)));
  border-left: var(--custom-input-border-left, var(--input-border, 1px solid var(--chart-100-pressed)));
  border-right: var(--custom-input-border-right, var(--input-border, 1px solid var(--chart-100-pressed)));
  width: 100%;
  height: var(--input-height);
  padding-right: 2px;
  background-color: var(--white-layout-301-level-0-elevation);
  font-family: var(--font-family-secondary);

  &.floating-label .input--label,
  &:focus-within .input--label {
    @include floating-label(-70%);
    top: 1px;
    display: flex;
    align-items: center;
  }

  &--prefixIcon {
    order: -1;
    width: var(--input-btn-width);
    height: var(--input-btn-height);
    display: inline-flex;
    justify-content: center;
    text-align: center;
    align-items: center;
    position: relative;
    background-color: transparent;
    color: var(--chart-302-icon-buttons-titles);
    margin: 0;
    padding: 0 0 0 var(--spacer);
    font-size: 16px;
  }

  &--field {
    display: block;
    padding: var(--input-padding);
    flex: 1;
    outline: none;
    border: none;
    box-shadow: none;
    color: var(--input-field-color, var(--body-text-arrows-400));
    background-color: transparent;
    cursor: text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: text;
    font-size: var(--body-size);
    font-family: var(--font-family-secondary);
    width: 100%;

    &::-webkit-autofill {
      background: red !important;
    }

    &:focus {
      &::placeholder {
        color: var(--chart-300-icon-buttons);
      }
    }
  }

  &.has-label {
    ::placeholder {
      color: transparent;
    }

    &:focus-within {
      ::placeholder {
        color: var(--chart-300-icon-buttons) !important;
      }
    }
  }

  ::placeholder {
    font-style: italic;
    text-overflow: ellipsis;
    color: var(--chart-300-icon-buttons);
    font-family: var(--font-family-secondary);
  }

  :host([active]) {
    .input {
      border-color: var(--blue-layout-301-active);
    }
  }

  &--label {
    position: absolute;
    pointer-events: none;

    color: var(--body-text-arrows-400);
    margin-left: var(--input-padding-left);
    font-size: var(--body-size);
    font-family: var(--font-family-secondary);
    transition: var(--floating-label-transition);
  }

  // visually hide text highlight for readonly input
  &--field[readonly] {
    cursor: pointer;
    &::selection {
      color: unset;
      background: none;
    }
  }

  &--button {
    width: var(--input-btn-width);
    height: var(--input-btn-height);
    display: inline-flex;
    justify-content: center;
    text-align: center;
    align-items: center;
    position: relative;
    background-color: transparent;
    color: var(--chart-302-icon-buttons-titles);
    margin: 0;
    font-size: 20px !important;
  }

  &--delete {
    display: inline-flex;
    border-radius: var(--border-radius);
    border: 1px solid transparent;
    cursor: pointer;

    &:hover {
      background-color: var(--chart-050-hover);
      border-color: var(--chart-050-hover);
    }

    &:focus {
      background-color: transparent;
      border-color: var(--blue-layout-500-focus);
    }
  }

  &:hover {
    border-color: var(--body-text-arrows-400);
  }

  &:focus-within {
    border-color: var(--blue-layout-301-active);
  }

  &:active {
    border-color: var(--blue-layout-301-active);
  }

  &:focus-within > .input--label {
    color: var(--blue-layout-301-active);
  }

  &:active > .input--label {
    color: var(--blue-layout-301-active);
  }

  &.invalid {
    border-color: var(--red-layout-300-primary);
  }

  &.invalid > .input--label {
    color: var(--red-layout-300-primary);
  }

  .ds-icon-validation_error {
    color: var(--red-layout-300-primary);
    font-size: 24px;
  }

  .ds-icon-validation_checked {
    color: var(--green-layout-100-primary-green);
    font-size: 24px;
  }
}

:host {
  height: initial;
}

:host([prefixIcon]) {
  .input {
    &--field {
      padding-left: var(--spacer);
    }
    .input--label {
      left: 20px;
    }
    &.floating-label .input--label,
    &:focus-within .input--label {
      left: 0;
    }
  }
}

:host([disabled]) {
  opacity: 0.5;
  pointer-events: none;

  &.input {
    &--delete {
      display: none;
    }
  }
}

:host([readonly]) {
  .input {
    border-color: transparent;
    background-color: var(--chart-050-hover);

    &--field {
      background-color: transparent;
      pointer-events: none;
    }

    &--delete {
      display: none;
    }

    &--button {
      display: none !important;
    }
  }

  .floating-label {
    .input--label {
      --padding-size: calc(var(--spacer) / 2);
      --default-margin-left: calc(var(--input-padding-left) - var(--padding-size));
      position: absolute;
      top: 1px;
      transform: translateY(-70%);
      margin-left: var(--floating-label-margin-left, var(--default-margin-left));
      padding: 0 var(--padding-size);
      max-width: 90%;
      font-size: var(--body-size-xsmall);
      text-overflow: ellipsis;
      overflow: hidden;

      &:before {
        background: transparent !important;
      }
    }
  }
}

:host([data-invalid]) {
  .input {
    border-color: var(--red-layout-300-primary);
  }
}

.counting-section {
  display: flex;
  color: var(--body-text-arrows-400);
  flex-direction: row;
  justify-content: end;
  font-size: var(--body-size);
  padding: calc(var(--spacer) / 2) calc(var(--spacer) / 2) 0 0;

  span {
    font-family: var(--font-family-secondary);
  }
}

:host(.max-chars-reached) {
  .counting-section,
  .input > .input--label {
    color: var(--red-layout-300-primary);
  }
  .input {
    border-color: var(--red-layout-300-primary);
  }
}
